import { BaseModel, Default, FormattedDateOfBirthToString } from '@red/data-access';
import { Expose, Transform, Type } from 'class-transformer';
import { ECDDManagementOwnershipType, EEcddCustomerType, EEcddFormStatus, EEcddGender, EEcddIdentityType, EEcddIdentityType2, EEcddLegalEntityType, EEcddResidentialStatus } from '../enums/ecdd.enum';
import { IEcdd, IEcddCustomer, IManagementOwnership } from '../interfaces/ecdd.interface';
import { IStatusDescription } from '../interfaces/status-description.interface';
import { CountryModel } from './country.model';
import { NationalityModel } from './nationality.model';
import { AddressModel } from './address.model';
import { IAmlClient } from '@shared/components/ingenique-api';
import { ECDD_FORM_STATUSES, ECDD_GENDER_OPTIONS, ECDD_IDENTIFY_TYPE_OPTIONS_2, ECDD_LEGAL_ENTITY_TYPE_OPTIONS, ECDD_RESIDENTIAL_STATUS_OPTIONS } from '../data/ecdd.data';
import { CheckListFormModel } from './ecdd-checklist.model';

export class EcddCustomerModel extends BaseModel implements IEcddCustomer {
  @Expose()
  id!: number;

  @Expose()
  uuid!: string;  // uuid of customer

  @Expose()
  ecddId!: number;

  @Expose()
  ecdd!: IEcdd;

  @Expose()
  type!: EEcddCustomerType;

  @Expose()
  formType!: string;

  @Expose()
  name!: string;

  @Expose()
  beneficialOwner!: string;

  @Expose()
  nricfin!: string;

  @Expose()
  idTypeId!: number;

  @Expose()
  idType!: unknown;

  @Expose()
  @FormattedDateOfBirthToString()
  dateOfBirth!: string;

  @Expose()
  @Transform(params => {
    if (!params.obj.dateOfBirth) {
      return '';
    }
    const dateOfBirth = params.obj.dateOfBirth
    const [datePart] = dateOfBirth.split('T');
    const [year, month, day] = datePart.split('-');
    const formattedDateOfBirth = `${day}-${month}-${year}`;
    return formattedDateOfBirth
  })
  dateOfBirthDDMMYYYY!: string;

  @Expose()
  @Transform(params => {
    if (!params.obj?.dateOfBirth) {
      return '';
    }
    const [datePart] = params.obj.dateOfBirth.split('T');
    if (!datePart) return '';
    const [year, month, day] = datePart.split('-');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const m = months[parseInt(month, 10) - 1] ?? '';
    return `${day} ${m} ${year}`;
  })
  dateOfBirthDDMMMYYYY!: string;

  @Expose()
  @Transform(params => {
    if (!params.obj.dateOfBirth) {
      return '';
    }
    const dateOfBirth = params.obj.dateOfBirth
    const [datePart] = dateOfBirth.split('T');
    const [year, month, day] = datePart.split('-');
    const formattedDateOfBirth = `${year}-${month}-${day}`;
    return formattedDateOfBirth
  })
  dateOfBirthYYYYMMDD!: string;

  @Expose()
  nationalityId!: number;

  @Expose()
  nationality!: string;

  @Expose()
  @Type(() => NationalityModel)
  nationalityObj!: NationalityModel;

  @Expose()
  occupation!: string;

  @Expose()
  countryId!: number;

  @Expose()
  country!: string;

  @Expose()
  @Type(() => CountryModel)
  countryObj!: CountryModel;

  @Expose()
  registrationNumber!: string;

  @Expose()
  identifyingType!: EEcddIdentityType | string;

  @Expose()
  mainBusiness!: string;

  @Expose()
  signature!: string;

  @Expose()
  gender!: string;

  @Expose()
  phone!: string;

  @Expose()
  email!: string;

  @Expose()
  @Type(() => AddressModel)
  residentialAddress?: AddressModel;

  @Expose()
  residentialStatus!: string;

  @Expose()
  @Type(() => AddressModel)
  addressPrincipal?: AddressModel;

  @Expose()
  identifyingTypeOther!: | string;

  @Expose()
  authorizedSignerName!: string;

  @Expose()
  signed!: boolean;

  @Expose()
  source!: string;

  @Expose()
  signedAt!: Date;

  @Expose()
  registeredOfficeAddress!: string;

  @Expose()
  principalAddressOfBusiness!: string;

  @Expose()
  companyIncorporationDate!: Date;

  @Expose()
  companyRegistrationNumber!: string;

  @Expose()
  legalEntityType!: EEcddLegalEntityType;

  @Expose()
  legalEntityTypeOther!: string;

  @Expose()
  mainBusinessActivity!: string;

  @Expose()
  kycMatch!: boolean;

  @Expose()
  kycSearchResult!: IAmlClient;

  @Expose()
  checklistFormC!: CheckListFormModel;

  @Expose()
  checklistFormU6!: CheckListFormModel;

  @Expose()
  @Transform(({ obj }) => {
    return obj?.checklistFormC && Object.keys(obj?.checklistFormC)?.length > 0 ? obj?.checklistFormC : obj?.checklistFormU6;
  })
  checklistForm!: CheckListFormModel;

  @Expose()
  ecddFileForm!: any;

  @Expose()
  formCStatus!: EEcddFormStatus;

  @Expose()
  formU6Status!: EEcddFormStatus;

  @Expose()
  @Transform(({ obj }) => {
    return obj?.formCStatus ? obj?.formCStatus : obj?.formU6Status;
  })
  formStatus!: EEcddFormStatus;

  @Expose()
  @Transform(({ obj }) => {
    return ECDD_FORM_STATUSES[(obj?.formCStatus ? obj?.formCStatus : obj?.formU6Status) as EEcddFormStatus];
  })
  displayFormCStatus!: IStatusDescription;

  @Expose()
  @Type(() => ManagementOwnershipModel)
  @Default([])
  managementOwnerships!: ManagementOwnershipModel[]

  @Expose()
  seniorManagementIsBeneficialOwner!: boolean;

  @Expose()
  isClientAuthorized!: boolean;

  @Expose()
  isMainCustomer!: boolean;

  @Expose()
  get identifyingTypeDisplay() {
    return ECDD_IDENTIFY_TYPE_OPTIONS_2[this.identifyingType as EEcddIdentityType2]?.viewValue ?? '';
  }

  @Expose()
  get genderDisplay() {
    return ECDD_GENDER_OPTIONS[this.gender as EEcddGender]?.viewValue ?? '';
  }

  @Expose()
  get residentialStatusDisplay() {
    return ECDD_RESIDENTIAL_STATUS_OPTIONS[this.residentialStatus as EEcddResidentialStatus]?.viewValue ?? this.residentialStatus;
  }

  @Expose()
  get kycMatchDisplay() {
    return this.kycMatch === false ? 'No Match' : '';
  }

  @Expose()
  get identifyingTypeOtherDisplay() {
    return this.identifyingType === EEcddIdentityType2.Other && this.identifyingTypeOther
      ? `(${this.identifyingTypeOther})`
      : '';
  }

  @Expose()
  get legalEntityTypeDisplay() {
    return ECDD_LEGAL_ENTITY_TYPE_OPTIONS[this.legalEntityType]?.viewValue ?? '';
  }

  @Expose()
  get legalEntityTypeOtherDisplay() {
    return this.legalEntityType === EEcddLegalEntityType.OTHER && this.legalEntityTypeOther
      ? `(${this.legalEntityTypeOther})`
      : '';
  }
}

export class ManagementOwnershipModel extends BaseModel implements IManagementOwnership {
  @Expose()
  id!: number;

  @Expose()
  uuid!: string;

  @Expose()
  type!: ECDDManagementOwnershipType;

  @Expose()
  name!: string;

  @Expose()
  identifyingType!: EEcddIdentityType | string;

  @Expose()
  identifyingTypeOther!: string;

  @Expose()
  country!: string;

  @Expose()
  occupation!: string;

  @Expose()
  @FormattedDateOfBirthToString()
  dateOfBirth!: string;

  @Expose()
  @Transform(params => {
    if (!params.obj.dateOfBirth) {
      return '';
    }
    const dateOfBirth = params.obj.dateOfBirth
    const [datePart] = dateOfBirth.split('T');
    const [year, month, day] = datePart.split('-');
    const formattedDateOfBirth = `${day}-${month}-${year}`;
    return formattedDateOfBirth
  })
  dateOfBirthDDMMYYYY!: string;

  @Expose()
  @Transform(params => {
    if (!params.obj?.dateOfBirth) {
      return '';
    }
    const [datePart] = params.obj.dateOfBirth.split('T');
    if (!datePart) return '';
    const [year, month, day] = datePart.split('-');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const m = months[parseInt(month, 10) - 1] ?? '';
    return `${day} ${m} ${year}`;
  })
  dateOfBirthDDMMMYYYY!: string;


  @Expose()
  nationality!: string;

  @Expose()
  nricfin!: string;

  @Expose()
  residentialAddress!: string;

  @Expose()
  residentialStatus!: string;

  @Expose()
  ecddId!: number;

  @Expose()
  customerId!: number;

  @Expose()
  @Type(() => EcddCustomerModel)
  customer?: EcddCustomerModel;

  @Expose()
  redocManagementOwnershipId!: number;

  @Expose()
  checklistFormC!: CheckListFormModel;

  @Expose()
  checklistFormU6!: CheckListFormModel;

  @Expose()
  @Transform(({ obj }) => {
    return obj?.checklistFormC && Object.keys(obj?.checklistFormC)?.length > 0 ? obj?.checklistFormC : obj?.checklistFormU6;
  })
  checklistForm!: CheckListFormModel;

  @Expose()
  ecddFileForm!: any;

  @Expose()
  formCStatus!: EEcddFormStatus;

  @Expose()
  formU6Status!: EEcddFormStatus;

  @Expose()
  @Transform(({ obj }) => {
    return obj?.formCStatus ? obj?.formCStatus : obj?.formU6Status;
  })
  formStatus!: EEcddFormStatus;

  @Expose()
  @Transform(({ obj }) => {
    return ECDD_FORM_STATUSES[(obj?.formCStatus ? obj?.formCStatus : obj?.formU6Status) as EEcddFormStatus];
  })
  displayFormCStatus!: IStatusDescription;

  @Expose()
  kycMatch!: boolean;

  @Expose()
  kycSearchResult!: IAmlClient;

  @Expose()
  get kycMatchDisplay() {
    return this.kycMatch === false ? 'No Match' : '';
  }

  @Expose()
  get identifyingTypeDisplay() {
    return ECDD_IDENTIFY_TYPE_OPTIONS_2[this.identifyingType as EEcddIdentityType2]?.viewValue ?? '';
  }

  @Expose()
  get identifyingTypeOtherDisplay() {
    return this.identifyingType === EEcddIdentityType2.Other && this.identifyingTypeOther
      ? `(${this.identifyingTypeOther})`
      : '';
  }
}